<template>
  <base-modal
    no-close-on-backdrop
    centered
    size="xl"
    :title="`Pagamento do frete do pedido ${orderId}`"
    :open.sync="open"
    :loading="loading"
  >
    <b-row v-if="loading">
      <b-col cols="12" class="text-center">
        <b-spinner label="Spinning"></b-spinner>
      </b-col>
    </b-row>
    <b-row v-else-if="!loading && !paymentUrl">
      <b-col cols="4" class="mx-auto mb-4">
        <b-card>
          <b-row>
            <b-col
              cols="3"
              class="d-flex align-items-center justify-content-center"
            >
              <i class="fas fa-truck fs-24"></i>
            </b-col>
            <b-col>
              <div>Valor do frete a pagar:</div>
              <div class="font-weight-bold fs-22">
                {{ formatNumber(shippingValue) }}
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
      <b-col cols="12" class="text-center mb-4">
        <span class="font-weight-bold fs-18">
          Selecione o meio de pagamento desejado
        </span>
      </b-col>
      <b-col
        cols="4"
        v-for="(method, index) in paymentMethods"
        :key="index"
        class="mb-2 c-pointer"
        @click="handlePaymentMethodSelected(method.type)"
      >
        <b-card
          :style="`border: ${
            selectedPaymentMethod == method.type
              ? '2px solid green'
              : '1px solid #ccc'
          }`"
        >
          <b-row>
            <b-col
              cols="5"
              class="d-flex align-items-center justify-content-center"
            >
              <b-img center fluid :src="method.logo"></b-img>
            </b-col>
            <b-col>
              <div class="font-weight-bold fs-14 mb-1">{{ method.name }}</div>
              <b-button
                size="sm"
                block
                :variant="
                  selectedPaymentMethod == method.type
                    ? 'success'
                    : 'outline-success'
                "
                :disabled="
                  !!selectedPaymentMethod &&
                  selectedPaymentMethod != method.type
                "
              >
                <i class="fas fa-money-check-alt mr-1"></i> Selecionar
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <b-alert :show="error" variant="danger" class="mt-4">
      <p>
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Houve um erro ao realizar o pagamento, verifique se sua integração com o
        Melhor Envio está habilitada em
        <b>"Meu aplicativo -> Frete e entrega"</b> (ou
        <a href="/area-restrita/lojista/frete-app" target="_blank">
          clique aqui </a
        >).
      </p>
      <p class="ml-4" v-if="errorInWallet">
        Verifique também se há saldo suficiente na sua carteira do Melhor Envio
        <a href="https://melhorenvio.com.br/painel" target="_blank">
          clicando aqui </a
        >.
      </p>
    </b-alert>

    <b-jumbotron
      class="py-4"
      bg-variant="tertiary"
      v-if="selectedPaymentMethod && paymentUrl"
    >
      <template #header>
        <div class="d-flex flex-column fs-12">
          <b-img
            fluid
            width="100px"
            :src="
              paymentMethods.find((x) => x.type === selectedPaymentMethod).logo
            "
          ></b-img>
          <span class="fs-12 mt-2">
            Finalize seu pagamento com
            {{
              paymentMethods.find((x) => x.type === selectedPaymentMethod).name
            }}!
          </span>
        </div>
      </template>

      <template #lead>
        Clique no botão "Acessar link de pagamento" abaixo para ser
        redirecionado para o
        {{ paymentMethods.find((x) => x.type === selectedPaymentMethod).name }}.
      </template>

      <hr class="my-4" />

      <p class="mb-2">
        Ou acesse o link <a :href="paymentUrl">{{ paymentUrl }}</a> ou consulte
        seu saldo diretamente na carteira do Melhor Envio,
        <a href="https://melhorenvio.com.br/painel/melhor-carteira">
          clicando aqui
        </a>.
      </p>

      <b-btn variant="outline-success" @click="openPaymentUrl()">
        <i class="fas fa-external-link-alt mr-2"></i>
        Acessar link de pagamento
      </b-btn>
      <b-btn
        variant="outline-info"
        class="ml-2"
        @click="
          () => {
            toggleOpen({});
            $emit('paid');
          }
        "
      >
        <i class="fas fa-check mr-2"></i>
        Já realizei o pagamento
      </b-btn>
    </b-jumbotron>

    <b-alert show variant="info" class="mt-4">
      <p>
        <i class="fas fa-info-circle mr-1"></i>
        Ao utilizar os meios de pagamento Mercado Pago, PicPay, PagSeguro e
        Moip, você será redirecionado para os respectivos meios de pagamentos
        para concluir a compra.
      </p>
      <p>
        <i class="fas fa-info-circle mr-1"></i>
        Caso opte pagar com o saldo na carteira do melhor envio não será
        necessário nenhuma outra ação.
      </p>
    </b-alert>

    <template #modal-footer>
      <b-btn variant="danger" @click="toggleOpen({})" :disabled="loading">
        <i class="fas fa-times mr-2"></i>
        Fechar
      </b-btn>
      <b-btn
        variant="success"
        @click="pay()"
        :disabled="loading || !selectedPaymentMethod || !!paymentUrl"
      >
        <i class="fas fa-check mr-2"></i>
        Realizar pagamento
      </b-btn>
    </template>
  </base-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import BaseModal from 'Components/Modal/BaseModal.vue';
import makeToast from 'Mixins/makeToast';

import { formatNumber } from '@/utils';

export default {
  name: 'OrderDetails',
  mixins: [makeToast],
  emits: ['paid'],
  data() {
    return {
      open: false,
      loading: false,
      error: false,
      errorInWallet: false,
      openedPaymentUrl: false,
      paymentSuccess: false,
      orderId: '',
      shippingValue: '',
      paymentUrl: '',
      selectedPaymentMethod: 0,
      paymentMethods: [
        {
          name: 'Saldo na carteira',
          type: 1,
          logo: 'https://construp-imagens.s3.us-east-2.amazonaws.com/logos/carteira.png',
          key: 'melhor-envio-wallet',
        },
        {
          name: 'Mercado Pago',
          type: 2,
          logo: 'https://construp-imagens.s3.us-east-2.amazonaws.com/logos/mercado-pago-logo.png',
          key: 'mercado-pago',
        },
        {
          name: 'PicPay',
          type: 3,
          logo: 'https://construp-imagens.s3.us-east-2.amazonaws.com/logos/picpay.png',
          key: 'picpay',
        },
        {
          name: 'Pag Seguro',
          type: 4,
          logo: 'https://construp-imagens.s3.us-east-2.amazonaws.com/logos/PagSeguro-Logo.png',
          key: 'pagseguro',
        },
        {
          name: 'Moip',
          type: 5,
          logo: 'https://construp-imagens.s3.us-east-2.amazonaws.com/logos/moip.png',
          key: 'moip',
        },
      ],
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
  },
  methods: {
    ...mapActions('integration', ['payMelhorEnvioFreight']),
    formatNumber,
    toggleOpen({ orderId = 0, shippingValue = 0 }) {
      this.clean();
      this.cleanPaymentFields();

      this.open = !this.open;
      this.orderId = orderId;
      this.shippingValue = shippingValue;
    },
    handlePaymentMethodSelected(type) {
      if (this.selectedPaymentMethod === type) {
        this.selectedPaymentMethod = 0;
        return;
      }

      this.selectedPaymentMethod = type;
    },
    pay() {
      let gateway = this.paymentMethods.find(
        (x) => x.type === this.selectedPaymentMethod
      ).key;

      const isMelhorEnvioWallet = gateway === 'melhor-envio-wallet';

      if (isMelhorEnvioWallet) gateway = '';

      this.error = false;
      this.errorInWallet = false;

      this.loading = true;

      this.payMelhorEnvioFreight({
        construpUserId: this.dadosUsuario.id,
        gateway,
        paymentId: this.orderId,
      })
        .then((response) => {
          const { paymentUrl, success } = response.data.data || {};

          this.paymentUrl = paymentUrl || '';
          this.paymentSuccess = success;

          if (!this.paymentUrl) {
            this.$emit('paid');
            this.toggleOpen({});
          }
        })
        .catch((error) => {
          this.error = true;

          if (isMelhorEnvioWallet) this.errorInWallet = true;

          this.makeToast(
            'Erro',
            `Houve um erro ao realizar o pagamento: ${error.response.data.message}`,
            'danger'
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openPaymentUrl() {
      if (!this.paymentUrl) return;

      window.open(this.paymentUrl, '_blank');

      this.clean();
      this.openedPaymentUrl = true;
    },
    clean() {
      this.orderId = '';
      this.shippingValue = '';
      this.error = false;
      this.errorInWallet = false;
    },
    cleanPaymentFields() {
      this.openedPaymentUrl = false;
      this.paymentSuccess = false;
      this.paymentUrl = '';
      this.selectedPaymentMethod = 0;
    },
  },
  components: {
    BaseModal,
  },
};
</script>
